/**
 * The Room, then the Record.
 *
 * Running the room links out to the existing present/TV surfaces rather than
 * rebuilding them — an offsite's big screen already exists and is better than
 * anything this app should reimplement. What this app adds is the part offsites
 * actually lose: the record. Decided, parked, and who owns what.
 *
 * Owners are written as canonical commitments, so "Dana owns the EU decision"
 * outlives the room it was said in.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Check, ExternalLink, MonitorPlay, PauseCircle, UserPlus } from 'lucide-react';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useCommitmentStore } from '@/stores/prioritization/commitmentStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import {
  closeAndComputeResults,
  openForParticipants,
  publishResults,
  sessionToolItems,
} from '../session/sessionAppActions';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { sessionPublishAuthorityLine, useSessionPublishAuthority } from '@/hooks/prioritization/useSessionPublishAuthority';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  PrimaryButton,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';

export default function RecordScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const app = getAppDefinition('offsite')!;
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);

  const fetchSessionById = useSessionStore((s) => s.fetchSessionById);
  const session = useSessionStore((s) => s.sessions.find((x) => x.id === sessionId));
  // Start, close and publish are drawn only for someone the session RPCs accept
  // (internal_session_publish_authority, migration 704) — build contract §2.8.
  const mayHost = useSessionPublishAuthority(session);
  const hostNames = useActorDisplayMap([session?.facilitatorId]);
  const hostName = session?.facilitatorId ? hostNames[session.facilitatorId] ?? null : null;
  const createCommitment = useCommitmentStore((s) => s.createCommitment);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ownerFor, setOwnerFor] = useState<string | null>(null);
  const [ownerName, setOwnerName] = useState('');
  const [owned, setOwned] = useState<Record<string, string>>({});

  useEffect(() => {
    if (sessionId) void fetchSessionById(sessionId);
  }, [sessionId, fetchSessionById]);

  const run = useCallback(
    async (action: () => Promise<{ success: boolean; error?: string }>) => {
      setBusy(true);
      setError(null);
      const result = await action();
      setBusy(false);
      if (!result.success) setError(result.error ?? 'Something went wrong.');
    },
    [],
  );

  const topics = useMemo(() => (session ? sessionToolItems(session) : []), [session]);

  /** Decided = above the line in the run; parked = everything else. */
  const { decided, parked } = useMemo(() => {
    const ranked = session?.rankingOutput?.rankedItemIds ?? [];
    if (ranked.length === 0) return { decided: [], parked: topics };
    const byId = new Map(topics.map((t) => [t.id, t]));
    const inOrder = ranked.map((id: string) => byId.get(id) ?? { id, name: id });
    // An offsite rarely resolves everything; the top half is what the room got to.
    const cut = Math.ceil(inOrder.length / 2);
    return { decided: inOrder.slice(0, cut), parked: inOrder.slice(cut) };
  }, [session, topics]);

  const assignOwner = async (topicId: string, topicName: string) => {
    if (!ownerName.trim()) return;
    setBusy(true);
    const created = await createCommitment({
      title: topicName,
      description: `Owned out of the offsite “${session?.name ?? ''}”.`,
      commitmentType: 'internal',
      status: 'active',
      ownerName: ownerName.trim(),
    });
    setBusy(false);
    if (created) {
      setOwned({ ...owned, [topicId]: ownerName.trim() });
      setOwnerFor(null);
      setOwnerName('');
    } else {
      setError('That owner could not be recorded.');
    }
  };

  if (!session) {
    return (
      <AppShell app={app} backTo="/apps/offsite">
        <EmptyState title="Loading offsite…" />
      </AppShell>
    );
  }

  const inRoom = session.state === 'CRITERIA_FINALIZED';
  const hasResult = (session.rankingOutput?.rankedItemIds?.length ?? 0) > 0;
  const host = mayHost ? 'you' : 'the host';

  return (
    <AppShell app={app} backTo="/apps/offsite" backLabel="Back to offsites">
      <ScreenHeading
        title={session.name}
        subtitle={`${topics.length} topics · ends with a record, not vibes`}
      />
      <BallLine
        ballWith={
          session.state === 'DRAFT'
            ? `${host} — start the room when everyone is there`
            : inRoom
              ? 'the room — decide together'
              : `${host} — name owners and publish the record`
        }
      />
      {!mayHost ? (
        <p className="mb-4 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
          {sessionPublishAuthorityLine('this offsite', hostName)}
        </p>
      ) : null}

      <div className="space-y-4">
        {session.state === 'DRAFT' && mayHost ? (
          <AppCard>
            <Eyebrow>Start the room</Eyebrow>
            <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
              Opens the offsite so everyone can weigh in from their phone while the big screen shows
              the room where it stands.
            </p>
            <div className="mt-4">
              <PrimaryButton
                disabled={busy}
                onClick={() =>
                  void run(() => openForParticipants(session, userId ?? '', userName ?? 'Organizer'))
                }
              >
                {busy ? 'Starting…' : 'Start the offsite'}
              </PrimaryButton>
            </div>
          </AppCard>
        ) : null}

        {inRoom ? (
          <AppCard>
            <Eyebrow>In the room</Eyebrow>
            <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
              Put the big screen up, and everyone weighs in from their own device.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <SecondaryButton
                onClick={() =>
                  window.open(`/prioritization/sessions/${session.id}/present`, '_blank')
                }
              >
                <MonitorPlay className="h-4 w-4" /> Open present mode
              </SecondaryButton>
              <SecondaryButton
                onClick={() => window.open(`/apps/session/${session.id}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4" /> Open as participant
              </SecondaryButton>
            </div>
            {mayHost ? (
              <div className="mt-4">
                <PrimaryButton disabled={busy} onClick={() => void run(() => closeAndComputeResults(session))}>
                  {busy ? 'Closing…' : 'Close the room'}
                </PrimaryButton>
              </div>
            ) : null}
          </AppCard>
        ) : null}

        {hasResult ? (
          <>
            <AppCard className="!p-0">
              <div className="px-5 pt-5">
                <Eyebrow>Decided · {decided.length}</Eyebrow>
              </div>
              <ul className="mt-2">
                {decided.map((t) => (
                  <li
                    key={t.id}
                    className="border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                  >
                    <div className="flex items-center gap-3">
                      <Check className="h-4 w-4 shrink-0 text-alpine-green-600" />
                      <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                        {t.name}
                      </span>
                      {owned[t.id] ? (
                        <StatusChip label={owned[t.id]} tone="green" />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setOwnerFor(ownerFor === t.id ? null : t.id)}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 font-body text-xs text-gray-600 hover:bg-gray-100 dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]"
                        >
                          <UserPlus className="h-3.5 w-3.5" /> Who owns it?
                        </button>
                      )}
                    </div>
                    {ownerFor === t.id ? (
                      <div className="mt-2 flex gap-2">
                        <input
                          value={ownerName}
                          onChange={(e) => setOwnerName(e.target.value)}
                          placeholder="Name"
                          className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
                        />
                        <PrimaryButton
                          onClick={() => void assignOwner(t.id, t.name)}
                          disabled={busy || !ownerName.trim()}
                          className="!px-3 !py-1.5 !text-xs"
                        >
                          Record
                        </PrimaryButton>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </AppCard>

            {parked.length > 0 ? (
              <AppCard className="!p-0">
                <div className="px-5 pt-5">
                  <Eyebrow>Parked · {parked.length}</Eyebrow>
                </div>
                <ul className="mt-2">
                  {parked.map((t) => (
                    <li
                      key={t.id}
                      className="flex items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-0 dark:border-[var(--rule)]"
                    >
                      <PauseCircle className="h-4 w-4 shrink-0 text-gray-400" />
                      <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                        {t.name}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="px-5 pb-4 pt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                  Parked isn&apos;t dropped — these stay on the record so the next offsite starts
                  where this one stopped.
                </p>
              </AppCard>
            ) : null}

            {session.state === 'RUN_COMPLETE' && mayHost ? (
              <div className="flex justify-end">
                <PrimaryButton disabled={busy} onClick={() => void run(() => publishResults(session.id))}>
                  {busy ? 'Publishing…' : 'Publish the record'}
                </PrimaryButton>
              </div>
            ) : null}
          </>
        ) : null}

        {error ? (
          <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
            {error}
          </p>
        ) : null}
      </div>
    </AppShell>
  );
}
